import type { ReactNode } from 'react';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useCurrentAuctionHouse } from '@/hooks/useCurrentAuctionHouse';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';
import { Navigate, useParams } from 'react-router';
import { ROUTES, type RouteKey } from '@/config/routes';
import { AccessLevel, hasAccess, getUserAccessLevel, routeAccess, type AccessLevelValue } from '@/config/access';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface RequireAccessPropsWithLevel {
  children: ReactNode;
  level: AccessLevelValue;
  route?: never;
  redirectTo?: string;
}

interface RequireAccessPropsWithRoute {
  children: ReactNode;
  level?: never;
  route: RouteKey;
  redirectTo?: string;
}

type RequireAccessProps = RequireAccessPropsWithLevel | RequireAccessPropsWithRoute;

/**
 * Unified access control guard component
 * Checks if user has required access level based on hierarchical system
 *
 * @param children
 * @param level - Explicit access level (for parent guards)
 * @param route - Route constant to look up access level from routeAccess (preferred)
 * @param redirectTo - Where to redirect if access denied (defaults to HOME)
 */
export const RequireAccess = ({ children, level, route, redirectTo = ROUTES.HOME }: RequireAccessProps) => {
  // Determine access level from route or use explicit level
  const requiredLevel = route ? routeAccess[route] : level!;

  const requiresAdminRole = requiredLevel <= AccessLevel.AUCTION_HOUSE_STAFF;
  const { isLoading: isAuthLoading, isBlocked } = useAuthGuard({ requireAdmin: requiresAdminRole });
  const { user } = useAuth();
  const { slug } = useParams<{ slug: string }>();

  // Always call hook (Rules of Hooks), but useCurrentAuctionHouse only fetches when slug exists
  const { auctionHouseId, isLoading: isAuctionHouseLoading, error: auctionHouseError } = useCurrentAuctionHouse();

  if (isAuthLoading) {
    return <LoadingOverlay />;
  }

  if (isBlocked) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // If on a slug route, verify access to that specific auction house
  if (slug) {
    // Still loading auction house data
    if (isAuctionHouseLoading) {
      return <LoadingOverlay />;
    }

    // If there's any error fetching the auction house (403, 404, network, etc), redirect to admin
    if (auctionHouseError) {
      return <Navigate to={ROUTES.ADMIN} replace />;
    }

    // Verify user has required access level for this specific auction house
    const userAccessLevel = getUserAccessLevel(user?.roles || [], auctionHouseId);
    if (!hasAccess(userAccessLevel, requiredLevel)) {
      return <Navigate to={ROUTES.ADMIN} replace />;
    }
  } else {
    // For non-slug routes, check if user has base admin role
    const userAccessLevel = getUserAccessLevel(user?.roles || [], undefined);
    if (!hasAccess(userAccessLevel, requiredLevel)) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};
